import React, { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import CrmSyncLog from "@/model/interface/CrmSyncLog";
import { useGetSyncLogsQuery, useGetSyncLogSummaryQuery, useRetrySyncLogMutation } from "@/store/rest_api";
import toast from "react-hot-toast";

interface Props {
  integrationId?: string;
}

export default function IntegrationSyncLog({ integrationId }: Props) {
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [page, setPage] = useState(0);

  const { data: summary } = useGetSyncLogSummaryQuery({ integrationId });
  const { data: logs, isLoading } = useGetSyncLogsQuery({
    status: statusFilter === "all" ? undefined : statusFilter,
    page,
    size: 20,
    integrationId,
  });
  const [retrySync] = useRetrySyncLogMutation();

  const handleRetry = async (logId: string) => {
    try {
      await retrySync(logId).unwrap();
      toast.success("Retry queued");
    } catch {
      toast.error("Retry failed");
    }
  };

  const statusBadge = (status: string) => {
    switch (status) {
      case "SYNCED":
        return <Badge variant="default">Synced</Badge>;
      case "FAILED":
        return <Badge variant="destructive">Failed</Badge>;
      case "PENDING":
        return <Badge variant="secondary">Pending</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  return (
    <div className="space-y-6">
      {summary && (
        <div className="grid grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summary.synced}</div>
              <p className="text-xs text-muted-foreground">Synced</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold text-destructive">{summary.failed}</div>
              <p className="text-xs text-muted-foreground">Failed</p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="text-2xl font-bold">{summary.pending}</div>
              <p className="text-xs text-muted-foreground">Pending</p>
            </CardContent>
          </Card>
        </div>
      )}

      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Sync Log</CardTitle>
              <CardDescription>Activity sync history between Retreevo and your CRM</CardDescription>
            </div>
            <Select value={statusFilter} onValueChange={(value) => { setStatusFilter(value); setPage(0); }}>
              <SelectTrigger className="w-32">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All</SelectItem>
                <SelectItem value="SYNCED">Synced</SelectItem>
                <SelectItem value="FAILED">Failed</SelectItem>
                <SelectItem value="PENDING">Pending</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Type</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>External ID</TableHead>
                <TableHead>Error</TableHead>
                <TableHead>Retries</TableHead>
                <TableHead>Date</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">Loading...</TableCell>
                </TableRow>
              )}
              {logs?.items.map((log: CrmSyncLog) => (
                <TableRow key={log.id}>
                  <TableCell>
                    <Badge variant="outline">{log.platformEntityType}</Badge>
                  </TableCell>
                  <TableCell>{statusBadge(log.status)}</TableCell>
                  <TableCell className="text-xs font-mono truncate max-w-[120px]">
                    {log.externalId || log.salesforceTaskId || "—"}
                  </TableCell>
                  <TableCell className="text-xs text-destructive truncate max-w-[200px]" title={log.errorMessage}>
                    {log.errorMessage || "—"}
                  </TableCell>
                  <TableCell>{log.retryCount}</TableCell>
                  <TableCell className="text-xs">
                    {new Date(log.createdAt).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    {log.status === "FAILED" && log.retryCount < 3 && (
                      <Button variant="outline" size="sm" onClick={() => handleRetry(log.id)}>
                        Retry
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
              {!isLoading && (!logs?.items || logs.items.length === 0) && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground">No sync logs</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>

          {logs && logs.totalPages > 1 && (
            <div className="flex justify-center gap-2 mt-4">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span className="text-sm self-center text-muted-foreground">
                Page {page + 1} of {logs.totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={logs.last}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
